import { activeCorners, isCorner, panelsOf, glassCornerOk } from "@/lib/geometry";

export const ANGLE_PRESETS = [90, 135, 150, 180];
const rad = Math.PI / 180;
const cum = (arr) => arr.reduce((a, v) => [...a, a[a.length - 1] + v], [0]);

export function cornerLines(item) {
  const xs = cum(item.columns), P = panelsOf(item);
  return xs.slice(1, -1).map((x, k) => {
    const i = k + 1;
    return { i, x, eligible: !P.some((p) => p.x0 < x && p.x1 > x), glassOk: glassCornerOk(item, i), k: item.corners?.[i] };
  });
}

export function withCouplings(items) {
  const byTag = new Map(items.map((i) => [i.tag, i]));
  return items.map((it) => {
    const out = {};
    ["left", "right"].forEach((side) => {
      const k = it.couplings?.[side], o = k?.tag && k.tag !== it.tag && byTag.get(k.tag);
      if (o) out[side] = { ...k, angle: +k.angle || 180, w: o.width, own: true };
    });
    items.forEach((o) => {
      if (o.id === it.id) return;
      if (o.couplings?.right?.tag === it.tag && !out.left) out.left = { ...o.couplings.right, angle: +o.couplings.right.angle || 180, tag: o.tag, w: o.width, own: false };
      if (o.couplings?.left?.tag === it.tag && !out.right) out.right = { ...o.couplings.left, angle: +o.couplings.left.angle || 180, tag: o.tag, w: o.width, own: false };
    });
    return Object.keys(out).length ? { ...it, _couple: out } : it;
  });
}

export const hasPlan = (item) => activeCorners(item).length > 0 || Object.values(item._couple || {}).some(isCorner);

export const turnOf = (k) => (isCorner(k) ? (180 - k.angle) * (k.dir === "int" ? 1 : -1) : 0);
const upright = (deg) => (deg > 90 ? deg - 180 : deg < -90 ? deg + 180 : deg);

export function planSketch(item) {
  const corners = activeCorners(item), cp = item._couple || {};
  const xs = [0, ...corners.map((k) => k.x), item.width];
  const th = (item.width + (cp.left?.w || 0) + (cp.right?.w || 0)) * 0.045;
  const P = [], pts = [];
  const dir = (h) => [Math.cos(h * rad), Math.sin(h * rad)];
  const seg = (a, b, h, text, nb) => {
    P.push({ t: "line", x1: a[0], y1: a[1], x2: b[0], y2: b[1], layer: nb ? "PLANNB" : "PLAN", weight: nb ? "thin" : "heavy", dash: nb, dxf: "PLAN" });
    const [dx, dy] = dir(h), n = [-dy, dx];
    P.push({ t: "text", x: (a[0] + b[0]) / 2 + n[0] * th * 1.1, y: (a[1] + b[1]) / 2 + n[1] * th * 1.1 + th * 0.3, text, h: th * 0.75, anchor: "middle", rot: upright(h), layer: "PLANTEXT", dxf: "PLAN" });
    pts.push(a, b);
  };
  const vertex = (p, h1, h2, k) => {
    const n1 = [Math.sin(h1 * rad), -Math.cos(h1 * rad)], n2 = [Math.sin(h2 * rad), -Math.cos(h2 * rad)], s = k.dir === "int" ? -1 : 1;
    const v = [n1[0] + n2[0], n1[1] + n2[1]], L = Math.hypot(...v) || 1;
    P.push({ t: "circle", cx: p[0], cy: p[1], r: th * 0.22, fill: "#BE185D", weight: "none", layer: "FOLD", dxf: "PLAN" });
    P.push({ t: "text", x: p[0] + (s * v[0] * th * 1.5) / L, y: p[1] + (s * v[1] * th * 1.5) / L + th * 0.3, text: `${k.angle}°`, h: th * 0.8, anchor: "middle", layer: "FOLDTEXT", bold: true, dxf: "PLAN" });
  };
  const widths = xs.slice(1).map((x, i) => x - xs[i]), longest = widths.indexOf(Math.max(...widths));
  const h0 = -corners.slice(0, longest).reduce((acc, k) => acc + turnOf(k), 0);
  let h = h0, pt = [0, 0];
  if (cp.left) {
    const hl = h0 - turnOf(cp.left), [dx, dy] = dir(hl), a = [-cp.left.w * dx, -cp.left.w * dy];
    seg(a, pt, hl, `${cp.left.tag} · ${cp.left.w}`, true);
    if (isCorner(cp.left)) vertex(pt, hl, h0, cp.left);
  }
  xs.slice(1).forEach((x, i) => {
    const [dx, dy] = dir(h), w = x - xs[i], b = [pt[0] + w * dx, pt[1] + w * dy];
    seg(pt, b, h, `${Math.round(w)}`);
    const k = corners[i];
    if (k) { const h2 = h + turnOf(k); vertex(b, h, h2, k); h = h2; }
    pt = b;
  });
  if (cp.right) {
    const h2 = h + turnOf(cp.right);
    if (isCorner(cp.right)) vertex(pt, h, h2, cp.right);
    const [dx, dy] = dir(h2);
    seg(pt, [pt[0] + cp.right.w * dx, pt[1] + cp.right.w * dy], h2, `${cp.right.tag} · ${cp.right.w}`, true);
  }
  const X = pts.map((p) => p[0]), Y = pts.map((p) => p[1]);
  const minX = Math.min(...X) - th * 2.2, maxX = Math.max(...X) + th * 2.2, minY = Math.min(...Y) - th * 2.2, maxY = Math.max(...Y) + th * 3.4;
  P.push({ t: "text", x: (minX + maxX) / 2, y: maxY - th * 0.4, text: "EXTERIOR", h: th * 0.7, anchor: "middle", layer: "PLANTEXT", dxf: "PLAN" });
  return { prims: P, bounds: { x: minX, y: minY, w: maxX - minX, h: maxY - minY } };
}
